/****************************************************************************************
 * JetStreamClient.ts                                                                   *
 *                                                                                      *
 * Reads JetStream “.fb” raster tiles using the 2025 FlatBuffers schema:                *
 *   /<time>/<altitude>/<zoom>/<x>/<y>.fb                                               *
 *                                                                                      *
 * Dependencies:                                                                        *
 *   - inversify (only for @injectable decorator)                                       *
 *   - lru-cache                                                                        *
 *   - a project-specific IHttpClient interface (Axios wrapper in this case)            *
 *   - FlatBuffers JS runtime + generated code for JetStreamDataTile.fbs                *
 ****************************************************************************************/

import * as flatbuffers from 'flatbuffers'
import { injectable } from 'inversify'
import { LRUCache } from 'lru-cache'

import { Result } from '@/domain/protocols/Result'

import type { IHttpClient } from '../http/IHttpClient'
import { JetStreamDataTile } from './JetStreamDataTile_generated'

import { HTTP_TIMEOUTS } from '@/infra/http/timeouts'

/*───────────────────────────── public types ─────────────────────────────*/

export interface JsRasterTileData {
  data: Uint8Array
}

export interface JsMetadata {
  version: number
  type: string
  id: string
  zooms: number[] // all zooms in metadata
  zoom: number // chosen zoom (highest)
  width: number
  height: number
  channelCount: number
  altitudes: number[]
  timestamps: number[]
  dataType: 'int8' | 'int16' | 'int32'
  fixedPointPrecision: number
}

export interface JsRequestData {
  latitude: number
  longitude: number
  altitude?: number // optional requested altitude
  timestamp?: number // optional requested time (UNIX seconds)
}

export interface JsSingleResult {
  data: Array<number | null>
  altitude: number
  timestamp: number
}

export interface JsBBoxResult {
  data: Array<number | null>[]
  altitude: number
  timestamp: number
}

/*───────────────────────────── JetStreamClient ──────────────────────────*/

@injectable()
export class JetStreamRasterClient {
  /* region is fixed → "bra" */
  private readonly region = 'bra'
  private metadata!: JsMetadata

  private tileCache: LRUCache<string, Promise<JsRasterTileData>>

  /**
   * @param httpClient  An injected HTTP client that implements IHttpClient
   * @param jetstreamUrl Base URL where JetStream tiles are hosted
   * @param layer        Layer (e.g. "wind-1000hpa")
   * @param cacheSize    Size of the LRU tile cache (defaults to 100)
   */
  constructor(
    private httpClient: IHttpClient,
    private jetstreamUrl: string,
    private layer: string,
    private cacheSize = 100
  ) {
    this.tileCache = new LRUCache<string, Promise<JsRasterTileData>>({
      max: this.cacheSize
    })

    /* kick off manifest fetch */
    this.fetchManifest().catch((err) => console.error('Failed to load JetStream manifest:', err))
  }

  /*────────────────────────── manifest & metadata ──────────────────────*/

  private async fetchManifest(): Promise<void> {
    const url = `${this.jetstreamUrl}/${this.region}/${this.layer}/metadata.fb`
    const res = await this.httpClient.get<ArrayBuffer>(url, {
      timeout: HTTP_TIMEOUTS.tile,
      responseType: 'arraybuffer'
    })
    if (!res.success) {
      throw new Error(`Failed to fetch manifest at ${url}`)
    }
    this.metadata = this.decodeMetaData(res.response)
  }

  private decodeMetaData(buffer: ArrayBuffer): JsMetadata {
    const bb = new flatbuffers.ByteBuffer(new Uint8Array(buffer))
    const fb = JetStreamDataTile.MetaData.getRootAsMetaData(bb)

    const zooms: number[] = []
    for (let i = 0; i < fb.zoomsLength(); i++) zooms.push(fb.zooms(i)!)
    const chosenZoom = Math.max(...zooms)

    const altitudes: number[] = []
    for (let i = 0; i < fb.altitudesLength(); i++) altitudes.push(fb.altitudes(i)!)

    const timestamps: number[] = []
    for (let i = 0; i < fb.timestampsLength(); i++) timestamps.push(Number(fb.timestamps(i)))

    return {
      version: fb.version(),
      type: fb.type() ?? 'fb',
      id: fb.id() ?? '',
      zooms,
      zoom: chosenZoom,
      width: fb.width(),
      height: fb.height(),
      channelCount: fb.channelCount(),
      altitudes,
      timestamps,
      dataType: (fb.dataType() as 'int8' | 'int16' | 'int32') ?? 'int8',
      fixedPointPrecision: fb.fixedPointPrecision()
    }
  }

  /*──────────────────────────── helpers ────────────────────────────────*/

  private decodeTile(buf: ArrayBuffer): JsRasterTileData {
    const bb = new flatbuffers.ByteBuffer(new Uint8Array(buf))
    const tile = JetStreamDataTile.Tile.getRootAsTile(bb)
    const len = tile.dataLength()
    const data = new Uint8Array(len)
    for (let i = 0; i < len; i++) data[i] = tile.data(i)!
    return { data }
  }

  private getDefaultAltitude(): number {
    return Math.min(...this.metadata.altitudes)
  }

  private getNoDataValue(): number {
    return this.metadata.dataType === 'int8' ? 0x7f : this.metadata.dataType === 'int16' ? 0x7fff : 0x7fffffff
  }

  private getClosestAltitude(a: number): number {
    return this.metadata.altitudes.reduce((best, v) => (Math.abs(v - a) < Math.abs(best - a) ? v : best))
  }

  private getClosestTime(t: number): number {
    return this.metadata.timestamps.reduce((best, v) => (Math.abs(v - t) < Math.abs(best - t) ? v : best))
  }

  private resolveAltitudeAndTime(requestAlt?: number, requestTs?: number) {
    const altitude = requestAlt !== undefined ? this.getClosestAltitude(requestAlt) : this.getDefaultAltitude()

    const nowS = Math.floor(Date.now() / 1000)
    const timestamp = requestTs !== undefined ? this.getClosestTime(requestTs) : this.getClosestTime(nowS)

    return { altitude, timestamp }
  }

  /*────────────────────────── URL & tile fetch ────────────────────────*/

  private getTileUrl(x: number, y: number, time: number, altitude: number): string {
    return `${this.jetstreamUrl}/${this.region}/${this.layer}/${time}/${altitude}/${this.metadata.zoom}/${x}/${y}.fb`
  }

  private async fetchTile(x: number, y: number, time: number, altitude: number): Promise<JsRasterTileData> {
    const key = `${time}-${altitude}-${this.metadata.zoom}-${x}-${y}`
    if (this.tileCache.has(key)) return this.tileCache.get(key)!

    const url = this.getTileUrl(x, y, time, altitude)
    const promise = this.httpClient
      .get<ArrayBuffer>(url, { responseType: 'arraybuffer', timeout: HTTP_TIMEOUTS.tile })
      .then((r) => {
        if (!r.success) throw new Error(`Tile fetch failed: ${url}`)
        return this.decodeTile(r.response)
      })

    this.tileCache.set(key, promise)
    return promise
  }

  private createTypedArray(tile: JsRasterTileData) {
    const { dataType } = this.metadata
    return dataType === 'int8'
      ? new Int8Array(tile.data.buffer, tile.data.byteOffset, tile.data.byteLength)
      : dataType === 'int16'
        ? new Int16Array(tile.data.buffer, tile.data.byteOffset, tile.data.byteLength / 2)
        : new Int32Array(tile.data.buffer, tile.data.byteOffset, tile.data.byteLength / 4)
  }

  /*──────────────────────── geo conversion ────────────────────────────*/

  private latLngToTileAndPixel(lat: number, lng: number) {
    const z = this.metadata.zoom
    const n = 2 ** z
    const xF = ((lng + 180) / 360) * n
    const latRad = (lat * Math.PI) / 180
    const yF = ((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * n

    const tileX = Math.floor(xF)
    const tileY = Math.floor(yF)

    const pixelX = Math.floor((xF - tileX) * this.metadata.width)
    const pixelY = Math.floor((yF - tileY) * this.metadata.height)

    return { tileX, tileY, pixelX, pixelY }
  }

  private tileLatLngBounds(tileX: number, tileY: number, zoom: number) {
    const n = 2 ** zoom
    const west = (tileX / n) * 360 - 180
    const east = ((tileX + 1) / n) * 360 - 180
    const north = (180 / Math.PI) * Math.atan(Math.sinh(Math.PI * (1 - (2 * tileY) / n)))
    const south = (180 / Math.PI) * Math.atan(Math.sinh(Math.PI * (1 - (2 * (tileY + 1)) / n)))

    return { north, south, east, west }
  }

  /*──────────────────────── public API – single ───────────────────────*/

  public async getData(opts: JsRequestData): Promise<Result<JsSingleResult>> {
    try {
      let fetchManifestRes
      if (!this.metadata) fetchManifestRes = await this.fetchManifest()

      const { altitude, timestamp } = this.resolveAltitudeAndTime(opts.altitude, opts.timestamp)
      const { tileX, tileY, pixelX, pixelY } = this.latLngToTileAndPixel(opts.latitude, opts.longitude)

      const tile = await this.fetchTile(tileX, tileY, timestamp, altitude)
      const arr = this.createTypedArray(tile)

      const base = (pixelY * this.metadata.width + pixelX) * this.metadata.channelCount
      const scale = 10 ** this.metadata.fixedPointPrecision
      const nod = this.getNoDataValue()
      const vals: Array<number | null> = []

      for (let c = 0; c < this.metadata.channelCount; c++) {
        const raw = arr[base + c]
        vals.push(raw === nod ? null : raw / scale)
      }

      return Result.ok({ data: vals, altitude, timestamp })
    } catch (error) {
      return Result.fail(`Failed to get data: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  /*──────────────────────── public API – multiple ────────────────────*/

  public async getDataFromMultiple(positions: JsRequestData[]): Promise<JsSingleResult[]> {
    if (!this.metadata) await this.fetchManifest()

    const out: JsSingleResult[] = new Array(positions.length)

    interface Group {
      list: { idx: number; px: number; py: number }[]
      altitude: number
      timestamp: number
      key: string
    }

    const groups: Record<string, Group> = {}

    positions.forEach((p, idx) => {
      const { altitude, timestamp } = this.resolveAltitudeAndTime(p.altitude, p.timestamp)
      const { tileX, tileY, pixelX, pixelY } = this.latLngToTileAndPixel(p.latitude, p.longitude)

      const key = `${timestamp}-${altitude}-${this.metadata.zoom}-${tileX}-${tileY}`

      if (!groups[key]) {
        groups[key] = {
          altitude,
          timestamp,
          key,
          list: []
        }
      }
      groups[key].list.push({ idx, px: pixelX, py: pixelY })
    })

    const scale = 10 ** this.metadata.fixedPointPrecision
    const nod = this.getNoDataValue()
    const w = this.metadata.width
    const cCount = this.metadata.channelCount

    await Promise.all(
      Object.values(groups).map(async (g) => {
        const [, , , xStr, yStr] = g.key.split('-')
        const tileX = parseInt(xStr, 10)
        const tileY = parseInt(yStr, 10)

        const tile = await this.fetchTile(tileX, tileY, g.timestamp, g.altitude)
        const arr = this.createTypedArray(tile)

        g.list.forEach(({ idx, px, py }) => {
          const base = (py * w + px) * cCount
          const vals: Array<number | null> = []
          for (let c = 0; c < cCount; c++) {
            const raw = arr[base + c]
            vals.push(raw === nod ? null : raw / scale)
          }
          out[idx] = { data: vals, altitude: g.altitude, timestamp: g.timestamp }
        })
      })
    )

    return out
  }

  /*──────────────────────── crop helper ───────────────────────────────*/

  /** Compute the pixel crop inside a tile that intersects a geographic box */
  private computeCropForTile(
    tileBounds: { north: number; south: number; east: number; west: number },
    boxSW: { latitude: number; longitude: number },
    boxNE: { latitude: number; longitude: number }
  ) {
    const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(v, max))

    const { width, height } = this.metadata

    // Clamp requested lon/lat to the tile bounds
    const startLng = clamp(boxSW.longitude, tileBounds.west, tileBounds.east)
    const endLng = clamp(boxNE.longitude, tileBounds.west, tileBounds.east)

    // Latitude is inverted (north > south)
    const startLat = clamp(boxNE.latitude, tileBounds.south, tileBounds.north)
    const endLat = clamp(boxSW.latitude, tileBounds.south, tileBounds.north)

    const fracXStart = (startLng - tileBounds.west) / (tileBounds.east - tileBounds.west)
    const fracXEnd = (endLng - tileBounds.west) / (tileBounds.east - tileBounds.west)

    const fracYStart = (tileBounds.north - startLat) / (tileBounds.north - tileBounds.south)
    const fracYEnd = (tileBounds.north - endLat) / (tileBounds.north - tileBounds.south)

    return {
      startX: Math.floor(fracXStart * width),
      endX: Math.ceil(fracXEnd * width),
      startY: Math.floor(fracYStart * height),
      endY: Math.ceil(fracYEnd * height)
    }
  }

  /*──────────────────── public API – bounding-box ─────────────────────*/

  /**
   * Fetches gridded data for a geographic bounding box.
   *
   * Returned `data` is a 2-D array of rows (south→north) where each element is
   *   - `null`  → no-data
   *   - `number`→ fixed-point-scaled value (already divided by 10^precision)
   */
  public async getDataFromBoundingBox(
    southwest: { latitude: number; longitude: number },
    northeast: { latitude: number; longitude: number },
    altitude?: number,
    time?: number
  ): Promise<JsBBoxResult> {
    if (!this.metadata) await this.fetchManifest()

    const { altitude: aChosen, timestamp: tChosen } = this.resolveAltitudeAndTime(altitude, time)

    /* work out which tiles intersect the box */
    const tl = this.latLngToTileAndPixel(northeast.latitude, southwest.longitude)
    const br = this.latLngToTileAndPixel(southwest.latitude, northeast.longitude)

    const minTileX = Math.min(tl.tileX, br.tileX)
    const maxTileX = Math.max(tl.tileX, br.tileX)
    const minTileY = Math.min(tl.tileY, br.tileY)
    const maxTileY = Math.max(tl.tileY, br.tileY)

    const rows: Array<number | null>[] = []
    const wTile = this.metadata.width
    const cCnt = this.metadata.channelCount
    const scale = 10 ** this.metadata.fixedPointPrecision
    const nod = this.getNoDataValue()

    /* iterate over intersecting tiles row by row (south → north) */
    for (let ty = minTileY; ty <= maxTileY; ty++) {
      /* vertical crop limits for this tile row */
      const rowTileBounds = this.tileLatLngBounds(minTileX, ty, this.metadata.zoom)
      const vCrop = this.computeCropForTile(rowTileBounds, southwest, northeast)

      const rowHeight = vCrop.endY - vCrop.startY
      if (rowHeight <= 0) continue // box misses this tile row

      /* prepare row buffers */
      const rowBuffers: Array<number | null>[] = Array.from({ length: rowHeight }, () => [] as Array<number | null>)

      for (let tx = minTileX; tx <= maxTileX; tx++) {
        const tileBounds = this.tileLatLngBounds(tx, ty, this.metadata.zoom)
        const crop = this.computeCropForTile(tileBounds, southwest, northeast)

        const cropW = crop.endX - crop.startX
        const cropH = crop.endY - crop.startY
        if (cropW <= 0 || cropH <= 0) {
          // Append nulls for the area this tile would occupy
          for (let r = 0; r < rowHeight; r++) {
            rowBuffers[r].push(...Array(cropW * cCnt).fill(null))
          }
          continue
        }

        /* fetch & decode tile */
        const tile = await this.fetchTile(tx, ty, tChosen, aChosen)
        const arr = this.createTypedArray(tile)

        /* copy pixels into rowBuffers */
        for (let y = crop.startY; y < crop.endY; y++) {
          const globalRow = y - vCrop.startY // index within rowBuffers

          for (let x = crop.startX; x < crop.endX; x++) {
            const base = (y * wTile + x) * cCnt
            for (let c = 0; c < cCnt; c++) {
              const raw = arr[base + c]
              rowBuffers[globalRow].push(raw === nod ? null : raw / scale)
            }
          }
        }
      }

      rows.push(...rowBuffers)
    }

    return { data: rows, altitude: aChosen, timestamp: tChosen }
  }
}
